import { SchoolReviewTabsEnum } from "@/enums/aem";
import {
  updateSchoolConnectsForDisplay,
  updateSchoolMeetingsForDisplay,
  updateSchoolQueriesForDisplay,
} from "@/methods/school";
import { showCustomMessage } from "@/methods/utils";
import { useModalStore } from "@/stores/modalStore";
import { useSessionStore } from "@/stores/sessionStore";
import {
  SchoolConnectDisplay,
  SchoolMonthlyMeetingDisplay,
  SchoolProfileDisplay,
  SchoolQueryDisplay,
  SchoolStore,
} from "@/types/admin";
import { defineStore, storeToRefs } from "pinia";
import { Component, markRaw } from "vue";

function getInitialSchoolStore(): SchoolStore {
  const initialSchoolStore: SchoolStore = {
    SchoolOptions: null,
    ActiveSchoolProfileId: null,
    SchoolProfileData: {},
    SchoolConnectData: {},
    SchoolQueryData: {},
    SchoolMonthlyMeetingData: {},
    AffiliationData: null,
    ActiveTab: null,
    SchoolDataLoading: false,
    SchoolCardsReloadKey: 0,
  };
  return JSON.parse(JSON.stringify(initialSchoolStore));
}

export const useSchoolStore = defineStore("schoolStore", {
  state: (): SchoolStore => getInitialSchoolStore(),
  getters: {
    ActiveSchoolProfile(): SchoolProfileDisplay | null {
      if (!this.ActiveSchoolProfileId) {
        return null;
      }
      return this.SchoolProfileData[this.ActiveSchoolProfileId];
    },
    ActiveSchoolConnects(): Array<SchoolConnectDisplay> | null {
      if (!this.ActiveSchoolProfileId) {
        return null;
      }
      return this.SchoolConnectData[this.ActiveSchoolProfileId];
    },
    ActiveSchoolQueries(): Array<SchoolQueryDisplay> | null {
      if (!this.ActiveSchoolProfileId) {
        return null;
      }
      return this.SchoolQueryData[this.ActiveSchoolProfileId];
    },
    ActiveSchoolMonthlyMeetings(): Array<SchoolMonthlyMeetingDisplay> | null {
      if (!this.ActiveSchoolProfileId) {
        return null;
      }
      return this.SchoolMonthlyMeetingData[this.ActiveSchoolProfileId];
    },
  },
  actions: {
    async initializeSchoolStore() {
      await this.getSchoolOptions();
    },
    async getSchoolOptions() {
      const sessionStore = useSessionStore();
      const { allApiVerified, UserInfo } = storeToRefs(sessionStore);
      if (this.SchoolOptions) {
        return;
      }
      const response = await allApiVerified.value.schoolProfileApi.getSchoolsForAem(
        UserInfo.value.UserId,
      );
      if (response.isSuccess) {
        this.SchoolOptions = response.data;
      } else {
        showCustomMessage({ messageText: response.errorMessage, messageType: "error" });
      }
    },
    async onSchoolChange(schoolProfileId: number | null) {
      this.ActiveSchoolProfileId = schoolProfileId;
      if (this.ActiveTab === null) {
        this.ActiveTab = SchoolReviewTabsEnum.SCHOOL_CONNECTS;
      }
      await this.getSchoolReviewDataForActiveTab();
    },
    async getSchoolReviewDataForActiveTab(forceReload: boolean = false) {
      this.SchoolDataLoading = true;
      if (
        this.ActiveTab === SchoolReviewTabsEnum.SCHOOL_CONNECTS &&
        (!this.ActiveSchoolConnects || forceReload)
      ) {
        await this.getSchoolConnectsForSchool();
      } else if (
        this.ActiveTab === SchoolReviewTabsEnum.SCHOOL_QUERIES &&
        (!this.ActiveSchoolQueries || forceReload)
      ) {
        await this.getSchoolQueriesForSchool();
      } else if (
        this.ActiveTab === SchoolReviewTabsEnum.SCHOOL_MONTHLY_MEETINGS &&
        (!this.ActiveSchoolMonthlyMeetings || forceReload)
      ) {
        await this.getSchoolMonthlyMeetingsBySchool();
      }
      this.SchoolCardsReloadKey += 1;
      this.SchoolDataLoading = false;
    },
    async getSchoolProfileForSchool() {
      const sessionStore = useSessionStore();
      const { allApiVerified } = storeToRefs(sessionStore);
      if (this.ActiveSchoolProfileId === null) {
        return;
      }
      const response = await allApiVerified.value.schoolProfileApi.getSchoolProfileForSchool(
        this.ActiveSchoolProfileId,
      );
      this.SchoolProfileData[this.ActiveSchoolProfileId] = response.isSuccess
        ? response.data
        : null;
    },
    async getSchoolConnectsForSchool() {
      if (this.ActiveSchoolProfileId === null) {
        return;
      }
      const sessionStore = useSessionStore();
      const { allApiVerified } = storeToRefs(sessionStore);
      const response = await allApiVerified.value.schoolProfileApi.getSchoolConnectsForSchool(
        this.ActiveSchoolProfileId,
      );
      this.SchoolConnectData[this.ActiveSchoolProfileId] = response.isSuccess ? response.data : [];
      const schoolConnects = this.SchoolConnectData[this.ActiveSchoolProfileId];
      await updateSchoolConnectsForDisplay(schoolConnects);
    },
    async getSchoolQueriesForSchool() {
      if (this.ActiveSchoolProfileId === null) {
        return;
      }
      const sessionStore = useSessionStore();
      const { allApiVerified } = storeToRefs(sessionStore);
      const response = await allApiVerified.value.schoolProfileApi.getSchoolQueriesForSchool(
        this.ActiveSchoolProfileId,
      );
      this.SchoolQueryData[this.ActiveSchoolProfileId] = response.isSuccess ? response.data : [];
      const schoolQueries = this.SchoolQueryData[this.ActiveSchoolProfileId];
      await updateSchoolQueriesForDisplay(schoolQueries);
    },
    async getSchoolMonthlyMeetingsBySchool() {
      if (this.ActiveSchoolProfileId === null) {
        return;
      }
      const sessionStore = useSessionStore();
      const { allApiVerified } = storeToRefs(sessionStore);
      const response = await allApiVerified.value.schoolProfileApi.getSchoolMonthlyMeetingsBySchool(
        this.ActiveSchoolProfileId,
      );
      this.SchoolMonthlyMeetingData[this.ActiveSchoolProfileId] = response.isSuccess
        ? response.data
        : [];
      const schoolMonthlyMeetings = this.SchoolMonthlyMeetingData[this.ActiveSchoolProfileId];
      await updateSchoolMeetingsForDisplay(schoolMonthlyMeetings);
    },
    onAddNewDetail(schoolReviewModal: Component) {
      const modalStore = useModalStore();
      const { openModal } = modalStore;
      openModal({ component: markRaw(schoolReviewModal), closeOnClickout: false });
    },
  },
});
